import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";

const PAGE_SIZE = 50;

// rows without a value for the sorted column (no USD price) go last in both orders
const compareRows = (column, order) => (a, b) => {
  const x = column.sortValue(a);
  const y = column.sortValue(b);
  if (x === null || y === null) return (x === null) - (y === null);
  const diff = typeof x === "string" ? x.localeCompare(y) : x - y;
  return order === "desc" ? -diff : diff;
};

/**
 * Sortable table of the explore pages; every row opens rowHref(row)
 * columns: [{ id, label, render(row, rank), sortValue?(row) -> number | string | null, align?, display? }]
 * display hides a column below a breakpoint, e.g. { xs: "none", md: "table-cell" }
 */
const ExploreTable = ({ columns, rows, rowHref, label, initialSort, loading, emptyText }) => {
  const navigate = useNavigate();
  const [sort, setSort] = useState(initialSort);
  const [visible, setVisible] = useState(PAGE_SIZE);

  const sorted = useMemo(() => {
    const column = columns.find((item) => item.id === sort.id);
    return column && column.sortValue ? [...(rows || [])].sort(compareRows(column, sort.order)) : rows || [];
  }, [columns, rows, sort]);

  const changeSort = (id) =>
    setSort((current) => ({ id, order: current.id === id && current.order === "desc" ? "asc" : "desc" }));

  const cellSx = (column) => ({
    display: column.display,
    textAlign: column.align || "right",
    borderColor: "divider",
    px: { xs: 1.5, sm: 2.5 },
    py: 2,
    whiteSpace: "nowrap",
  });

  return (
    <Box>
      <Box sx={{ overflowX: "auto" }}>
        <Table aria-label={label} sx={{ tableLayout: "auto" }}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  sortDirection={sort.id === column.id ? sort.order : false}
                  sx={{ ...cellSx(column), py: 1.5, color: "text.secondary", fontWeight: 400, fontSize: "0.875rem" }}
                >
                  {column.sortValue ? (
                    <TableSortLabel
                      active={sort.id === column.id}
                      direction={sort.id === column.id ? sort.order : "desc"}
                      onClick={() => changeSort(column.id)}
                      sx={{ flexDirection: column.align === "left" ? "row" : "row-reverse" }}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? Array.from({ length: 8 }, (_, index) => (
                  <TableRow key={index}>
                    {columns.map((column) => (
                      <TableCell key={column.id} sx={cellSx(column)}>
                        <Skeleton
                          width={column.align === "left" ? "70%" : "50%"}
                          sx={{ ml: column.align === "left" ? 0 : "auto" }}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : sorted.slice(0, visible).map((row, index) => (
                  <TableRow
                    key={row.id}
                    hover
                    // the name cell holds the link (keyboard, new tab); a click anywhere else on the row opens it too
                    onClick={(event) => {
                      if (!event.target.closest("a")) navigate(rowHref(row));
                    }}
                    sx={{ cursor: "pointer", "&.MuiTableRow-hover:hover": { bgcolor: "background.light" } }}
                  >
                    {columns.map((column) => (
                      <TableCell key={column.id} sx={cellSx(column)}>
                        {column.render(row, index + 1)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </Box>
      {!loading && sorted.length === 0 ? (
        <Typography sx={{ px: 2.5, py: 4, color: "text.secondary", textAlign: "center" }}>{emptyText}</Typography>
      ) : null}
      {!loading && sorted.length > visible ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <Button onClick={() => setVisible(visible + PAGE_SIZE)}>
            Show {Math.min(PAGE_SIZE, sorted.length - visible)} more
          </Button>
        </Box>
      ) : null}
    </Box>
  );
};

export default ExploreTable;
